import React from "react";
import { Outlet, useParams } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";

import { BaseLayout } from "./BaseLayout";
import { PageContainer } from "../components/PageContainer/PageContainer";
import { ProfileHeader, getFullName } from "../components/ProfileHeader/ProfileHeader";
import { LoadingSpinner } from "../components/LoadingSpinner/LoadingSpinner";
import { Paragraph } from "../components/Typography/Typography";
import { ProfileProvider } from "../contexts/ProfileContext";
import { useProfileTabData } from "../hooks/useProfileTabData";
import { fetchClerkUserByUsername, ClerkUser } from "../api/clerk";
import { getFollowers, getFollowStatus } from "../api/follows";
import { fetchAlbumStats } from "../api/albums";
import { useFriendExchangeTabData } from "../hooks/useFriendExchangeTabData";

import "../pages/Profile.scss";

export function ProfileLayout() {
  const { username } = useParams();
  const { user, isLoaded } = useUser();

  const [userData, setUserData] = React.  useState<ClerkUser | null>(null);
  const [friends, setFriends] = React.useState<ClerkUser[]>([]);
  const [isLoadingFriends, setIsLoadingFriends] = React.useState(true);
  const [albumsSharedCount, setAlbumsSharedCount] = React.useState(0);
  const [albumsListenedCount, setAlbumsListenedCount] = React.useState(0);
  const [isLoadingProfile, setIsLoadingProfile] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [isFriend, setIsFriend] = React.useState(false);

  const isCurrentUser = isLoaded && user?.username === username;
  const showFriendExchange = isLoaded && Boolean(user) && !isCurrentUser && isFriend;

  const {
    sent,
    sentSubtitleByAlbumId,
    isLoadingSharedTab,
    listenTabData,
    queueRecommendations,
    doneRecommendations,
    usersByExternalId,
    isLoadingListenTab,
    refreshSharedTab,
    refreshListenTab,
  } = useProfileTabData(userData?.externalId, isCurrentUser);

  const {
    received: receivedFromFriend,
    sent: sentToFriend,
    isLoading: isLoadingFriendExchange,
  } = useFriendExchangeTabData(userData?.externalId, showFriendExchange);

  const refreshFriends = React.useCallback(async () => {
    if (!userData?.externalId) {
      setFriends([]);
      setAlbumsSharedCount(0);
      setAlbumsListenedCount(0);
      setIsLoadingFriends(false);
      return;
    }

    setIsLoadingFriends(true);

    try {
      const [friendsResponse, statsResponse] = await Promise.all([
        getFollowers(userData.externalId),
        fetchAlbumStats(userData.externalId),
      ]);
      setFriends(friendsResponse);
      setAlbumsSharedCount(statsResponse.shared_album_count);
      setAlbumsListenedCount(statsResponse.listened_album_count);
    } finally {
      setIsLoadingFriends(false);
    }
  }, [userData?.externalId]);

  React.useEffect(() => {
    const getUserData = async () => {
      if (!username) {
        return;
      }

      setIsLoadingProfile(true);
      setLoadError(null);

      try {
        const response = await fetchClerkUserByUsername(username);
        setUserData(response);
      } catch (error) {
        console.error("Error loading profile:", error);
        setLoadError("Could not load profile. Make sure the API server is running.");
        setUserData(null);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    getUserData();
  }, [username]);

  React.useEffect(() => {
    refreshFriends();
  }, [refreshFriends]);

  React.useEffect(() => {
    if (!isLoaded || isCurrentUser || !user?.externalId || !userData?.externalId) {
      setIsFriend(false);
      return;
    }

    let cancelled = false;

    getFollowStatus({
      followerUserId: user.externalId,
      followeeUserId: userData.externalId,
    }).then(({ status }) => {
      if (!cancelled) {
        setIsFriend(status === "accepted");
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isLoaded, isCurrentUser, user?.externalId, userData?.externalId]);

  if (isLoadingProfile) {
    return (
      <BaseLayout className="profile-page">
        <PageContainer>
          <LoadingSpinner />
        </PageContainer>
      </BaseLayout>
    );
  }

  if (!userData) {
    return (
      <BaseLayout className="profile-page">
        <PageContainer>
          <Paragraph variant="body-2">{loadError ?? "User not found."}</Paragraph>
        </PageContainer>
      </BaseLayout>
    );
  }

  const fullName = getFullName(userData);

  return (
    <BaseLayout className="profile-page">
      <PageContainer>
        <ProfileProvider
          value={{
            userData,
            friends,
            followersCount: friends.length,
            isLoadingFriends,
            isCurrentUser,
            isLoaded,
            fullName,
            refreshFriends,
            sent,
            sentSubtitleByAlbumId,
            isLoadingSharedTab,
            listenTabData,
            queueRecommendations,
            doneRecommendations,
            usersByExternalId,
            isLoadingListenTab,
            refreshSharedTab,
            refreshListenTab,
            isFriend,
            receivedFromFriend,
            sentToFriend,
            isLoadingFriendExchange,
          }}
        >
          <ProfileHeader
            userData={userData}
            followersCount={friends.length}
            albumsSharedCount={albumsSharedCount}
            albumsListenedCount={albumsListenedCount}
            isCurrentUser={isCurrentUser}
            isLoaded={isLoaded}
            showHistoryTab={showFriendExchange}
          />
          <Outlet />
        </ProfileProvider>
      </PageContainer>
    </BaseLayout>
  );
}
